<template>
  <div class="mdc-text-field-container">
    <div
      class="mdc-text-field"
      :class="styleClasses"
    >
      <i
        v-if="leadingIcon"
        class="material-icons mdc-text-field__icon"
        v-text="leadingIcon"
      />
      <i
        v-if="trailingIcon && trailingIconType === 'icon'"
        class="material-icons mdc-text-field__icon"
        v-text="trailingIcon"
      />

      <img
        v-else
        class="mdc-text-field__icon"
        :src="trailingIcon"
      >

      <input
        v-if="!isTextArea"
        :id="id"
        :type="type"
        class="mdc-text-field__input"
        :name="name"
        :placeholder="!outlined && label"
        :autocomplete="autocomplete"
        :aria-label="!outlined && label"
        :required="required"
        :minlength="minlength"
        :maxlength="maxlength"
        :min="min"
        :max="max"
        :step="step"
        :pattern="pattern"
        :value="value"
        @input="onInput"
        @focus="onFocus"
        @blur="onBlur"
        @change="onChange"
      >

      <textarea
        v-else
        :id="id"
        class="mdc-text-field__input"
        :rows="rows"
        :cols="cols"
        :value="value"
        :required="required"
        @input="onInput"
        @change="onChange"
        @focus="onFocus"
        @blur="onBlur"
      />

      <div
        v-show="outlined"
        class="mdc-notched-outline"
      >
        <div class="mdc-notched-outline__leading" />
        <div class="mdc-notched-outline__notch">
          <label
            :for="id"
            class="mdc-floating-label"
            v-text="label"
          />
        </div>
        <div class="mdc-notched-outline__trailing" />
      </div>
    </div>
    <mdc-text-field-helper-text
      :help-message="helpText"
      :error-message="errorMessage"
    />
  </div>
</template>

<script>
// import * as _ from 'lodash'

// https://material.io/develop/web/components/input-controls/text-field/
import { MDCTextField } from '@material/textfield'
import MdcTextFieldHelperText from './TextFieldHelperText'

export default {
  name: 'MaterialTextField',
  components: {
    MdcTextFieldHelperText
  },
  props: {
    id: {
      type: String,
      default: ''
    },
    value: {
      type: String,
      default: ''
    },
    name: {
      type: String,
      default: undefined
    },
    autocomplete: {
      type: String,
      default: undefined
    },
    type: {
      type: String,
      default: 'text'
    },
    outlined: {
      type: Boolean,
      default: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    minlength: {
      type: Number,
      default: undefined
    },
    maxlength: {
      type: Number,
      default: undefined
    },
    min: {
      type: Number,
      default: undefined
    },
    max: {
      type: Number,
      default: undefined
    },
    step: {
      type: Number,
      default: undefined
    },
    rows: {
      type: Number,
      default: undefined
    },
    cols: {
      type: Number,
      default: undefined
    },
    pattern: {
      type: String,
      default: undefined
    },
    fullWidth: {
      type: Boolean,
      default: true
    },
    leadingIcon: {
      type: String,
      default: ''
    },
    trailingIcon: {
      type: String,
      default: ''
    },
    trailingIconType: {
      type: String,
      default: 'icon'
    },
    helpText: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      default: ''
    },
    validate: {
      type: Function,
      default () {
        return { valid: true, message: '' }
      }
    },
    format: {
      type: Function,
      default (v) {
        return v
      }
    }
  },
  data () {
    return {
      initializationRequired: false,
      errorMessage: ''
    }
  },
  computed: {
    isTextArea () {
      return this.type === 'textarea'
    },
    styleClasses () {
      return {
        'mdc-text-field--fullwidth': !this.outlined && this.fullWidth,
        'mdc-text-field--outlined-fullwidth': this.outlined && this.fullWidth,
        'mdc-text-field--outlined': this.outlined,
        'mdc-text-field--disabled': this.disabled,
        'mdc-text-field--with-leading-icon': !!this.leadingIcon,
        'mdc-text-field--with-trailing-icon': !!this.trailingIcon,
        'mdc-text-field--textarea': this.isTextArea
      }
    }
  },
  watch: {
    type (val, prev) {
      if (val !== prev) {
        this.initializationRequired = true
      }
    },
    value (val) {
      this.mdcTextField.value = val
    }
  },
  updated () {
    if (this.initializationRequired) {
      this.destroyMDC()
      this.initMDC()
      this.initializationRequired = false
    }
  },
  mounted () {
    this.initMDC()
  },
  destroyed () {
    this.destroyMDC()
  },
  methods: {
    initMDC () {
      this.mdcTextField = new MDCTextField(this.$el.querySelector('.mdc-text-field'))
      this.mdcTextField.useNativeValidation = true
    },
    destroyMDC () {
      this.mdcTextField && this.mdcTextField.destroy()
    },
    async onInput ({ target }) {
      if (!this.mdcTextField.valid) {
        this.setValidity({ valid: true, message: '' })
      }

      this.mdcTextField.value = await this.format(target.value)
      this.$emit('input', this.mdcTextField.value)
    },
    async onChange ({ target }) {
      if (!this.mdcTextField.valid) {
        this.setValidity({ valid: true, message: '' })
      }

      this.mdcTextField.value = await this.format(target.value)
      this.$emit('change', this.mdcTextField.value)
    },
    async onFocus () {
      this.$emit('focus')
    },
    async onBlur () {
      await this.checkValidity()
      this.$emit('blur')
    },
    async checkValidity () {
      this.setValidity(await this.validate(this.mdcTextField.value))
    },
    layout () {
      this.mdcTextField.layout()
    },
    setValidity ({ valid, message }) {
      this.hasErrors = !valid
      this.mdcTextField.valid = valid
      this.mdcTextField.input_.setCustomValidity(!valid ? message : '')
      // this.mdcTextField.helperTextContent = !valid ? message : ''
      this.errorMessage = !valid ? message : ''
      this.$emit('validity', { valid, message })
    }
  }
}
</script>

<style lang="scss">
@import "./variables";
@import "@material/textfield/mdc-text-field";
@import "@material/textfield/helper-text/mdc-text-field-helper-text";
.mdc-text-field-container {
  width: 100%;
}

.mdc-text-field--outlined-fullwidth {
  width: 100%;
}

img.mdc-text-field__icon {
  max-height: 25px;
}
</style>
